import { useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, Button, Textarea, Progress, ReversePaginatedList } from "@tellescope/ui";
import { Send, Paperclip, ArrowLeft, X, FileIcon, Loader2 } from "lucide-react";
import type { ChatAttachment } from "@tellescope/types-models";
import { useUploadTellescopeFile, useTellescopeQuery } from "@tellescope/tanstack-query";
import { useEnduserChatMessages } from "../../../hooks/useEnduserChatMessages";
import { useSendChatMessage } from "../../../hooks/useSendChatMessage";
import { useEnduserCurrentEnduser, useEnduserStore } from "../../../store/hooks";
import { MessageBubble, EmptyMessages } from "../../../components/chat";
import { getAttachmentType } from "@tellescope/utils";
import { PageSkeleton } from "./PageSkeleton";

interface PendingAttachment {
  file: File;
  name: string;
}

/**
 * Full-page chat room view for the /communications/:roomId route.
 * Adapts the ChatDialog component to a standalone page layout.
 */
export const ChatRoomViewPage = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const { t } = useTranslation("messages");
  const currentEnduser = useEnduserCurrentEnduser();
  const session = useEnduserStore((state) => state.session);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Message input state
  const [messageText, setMessageText] = useState("");
  const [pendingAttachment, setPendingAttachment] = useState<PendingAttachment | null>(null);

  // Hooks
  const chatRoomQuery = useTellescopeQuery({
    model: "chat_rooms",
    method: "getOne",
    request: roomId ?? "",
    enabled: !!roomId,
  });
  const chatRoom = chatRoomQuery.data ?? null;
  const roomLoading = chatRoomQuery.isLoading;
  const roomError = chatRoomQuery.error;

  const {
    messages,
    isLoading: messagesLoading,
    error: messagesError,
  } = useEnduserChatMessages({
    roomId: roomId ?? "",
    enabled: !!roomId,
  });

  const { sendMessageAsync, isPending: isSending } = useSendChatMessage();

  const {
    uploadFileAsync,
    stage: uploadStage,
    progress: uploadProgress,
    reset: resetUpload,
  } = useUploadTellescopeFile({
    session,
  });

  const isUploading = uploadStage === "preparing" || uploadStage === "uploading" || uploadStage === "confirming";
  const canSend = (messageText.trim() || pendingAttachment) && !isSending && !isUploading;

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPendingAttachment({
      file,
      name: file.name,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleRemoveAttachment = useCallback(() => {
    setPendingAttachment(null);
    resetUpload();
  }, [resetUpload]);

  const handleSend = useCallback(async () => {
    if (!roomId || !canSend) return;

    try {
      let attachments: ChatAttachment[] | undefined;

      if (pendingAttachment) {
        const uploadResult = await uploadFileAsync(
          {
            name: pendingAttachment.name,
            size: pendingAttachment.file.size,
            type: pendingAttachment.file.type,
          },
          pendingAttachment.file
        );

        attachments = [
          {
            type: getAttachmentType(pendingAttachment.file.type),
            name: pendingAttachment.name,
            secureName: uploadResult.secureName,
          },
        ];
      }

      const message = messageText.trim() || (pendingAttachment ? t("Sent an attachment") : "");
      if (!message && !attachments) return;

      await sendMessageAsync({
        roomId,
        message,
        attachments,
      });

      setMessageText("");
      setPendingAttachment(null);
      resetUpload();
    } catch {
      // Error is handled by the mutation
    }
  }, [roomId, canSend, messageText, pendingAttachment, t, uploadFileAsync, sendMessageAsync, resetUpload]);

  const isLoading = roomLoading || messagesLoading;
  const error = roomError || messagesError;
  const displayTitle = chatRoom?.title || chatRoom?.topic || t("Conversation");

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (error || !roomId) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="border-b bg-background">
          <div className="container mx-auto max-w-3xl px-4 py-3 flex items-center gap-3">
            <Link to="/communications">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <h1 className="text-lg font-semibold">{t("Conversation")}</h1>
          </div>
        </div>
        <div className="flex-1 p-4">
          <Card className="border-destructive">
            <CardContent className="p-4 text-center text-destructive text-sm">
              {t("Failed to load messages")}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="border-b bg-background flex-shrink-0">
        <div className="container mx-auto max-w-3xl px-4 py-3 flex items-center gap-3">
          <Link to="/communications">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-lg font-semibold truncate">{displayTitle}</h1>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-hidden">
        <div className="container mx-auto max-w-3xl h-full">
          {messages.length === 0 ? (
            <EmptyMessages />
          ) : (
            <ReversePaginatedList className="h-full p-4">
              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isOwnMessage={!!currentEnduser?.id && message.senderId === currentEnduser.id}
                />
              ))}
            </ReversePaginatedList>
          )}
        </div>
      </div>

      {/* Reply input */}
      <div className="border-t bg-background flex-shrink-0">
        <div className="container mx-auto max-w-3xl p-3">
          {/* Attachment preview */}
          {pendingAttachment && (
            <div className="mb-2 p-2 bg-muted rounded-lg">
              <div className="flex items-center gap-2">
                <FileIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm truncate flex-1">{pendingAttachment.name}</span>
                {isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={handleRemoveAttachment}
                    title={t("Remove attachment")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {isUploading && <Progress value={uploadProgress} className="mt-2 h-1" />}
            </div>
          )}

          {/* Input row */}
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelect}
              disabled={isUploading || isSending}
            />

            <Button
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0 flex-shrink-0"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || isSending || !!pendingAttachment}
              title={t("Attach file")}
            >
              <Paperclip className="h-4 w-4 text-muted-foreground" />
            </Button>

            <Textarea
              placeholder={t("Type a message...")}
              className="min-h-[40px] max-h-[120px] resize-none"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              disabled={isSending || isUploading}
              rows={1}
            />

            <Button
              size="sm"
              className="h-10 w-10 p-0 flex-shrink-0"
              onClick={handleSend}
              disabled={!canSend}
              title={t("Send message")}
            >
              {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
